/**
 * Budget plans — the shape Ré's `create_budget` tool hands to
 * `create_budget_from_plan` (migrations `20260917170135` and `20260918094948`),
 * validated once on the API side before the RPC ever runs.
 *
 * ── TWO KINDS, ONE SCHEMA ───────────────────────────────────────────────────
 * `envelope_budgets.budget_type` has two values and they take DIFFERENT plans:
 *
 *   'envelope'  a recurring budget: one monthly `default_budgeted` per category
 *               plus SPARSE per-month overrides (`months`, a `{ 'YYYY-MM':
 *               amount }` map on the wire — the handler unrolls it into the
 *               RPC's `[{ month, budgeted }]` array). Rollover applies.
 *   'total'     a fixed/project budget: ONE `total_budgeted` per category for
 *               the whole period. No monthly amounts, no overrides, no
 *               rollover — and `end_month` is REQUIRED, because the figure only
 *               means anything against a defined period.
 *
 * So the plan is a DISCRIMINATED UNION on `budget_type`, wrapped in a
 * `z.preprocess` that supplies `'envelope'` when the key is absent. That
 * wrapper is load-bearing, not stylistic: `ZodDiscriminatedUnion` reads the
 * discriminator off the RAW input, so an absent key would fail with
 * `invalid_union_discriminator` — and every plan shape written before
 * 2026-09-18 omits it. `== null` in the preprocess catches an explicit `null`
 * too, which a model does emit.
 *
 * The wrong-side fields are declared `z.undefined({ invalid_type_error })`
 * rather than left to `.strict()`: the model reads the message, and
 * "Unrecognized key(s) in object" tells it nothing about which field to use
 * instead. Zod drops an absent key from the parsed output entirely, so these
 * phantom properties never reach the wire.
 *
 * ── SCOPE ───────────────────────────────────────────────────────────────────
 * `project_id` (optional) scopes the budget to one project of the caller's org;
 * actuals then filter on `ledger_transactions.project_id`. The API verifies the
 * id in the caller's org and the RPC re-checks it. Fund-scoped budgets are NOT
 * expressible here: `fund_id` is a DECLARED key that accepts only `null`, so a
 * real fund id comes back with a message aimed at the model rather than
 * `.strict()`'s "Unrecognized key(s)". The RPC refuses it too.
 *
 * ── TOP-DOWN RECONCILIATION ─────────────────────────────────────────────────
 * `expected_expense_total` (total plans only) is the grand total the USER gave
 * ("a €60,000 budget for the festival"); the non-income lines must add up to it
 * within `BUDGET_PLAN_TOTAL_TOLERANCE`. It is checked HERE AND ONLY HERE
 * (ruling, 2026-09-18): `create_budget_from_plan` has exactly one caller, so a
 * plpgsql twin would re-check the same model-authored numbers a second time.
 * The handler does not forward the key, and the RPC never sees it.
 *
 * ── BOUNDS ──────────────────────────────────────────────────────────────────
 * `BUDGET_PLAN_LIMITS` is sized so the worst-case `create_budget` tool-call
 * arguments stay around 1,500 tokens: the bookkeeper's per-turn
 * `MAX_COMPLETION_TOKENS` also pays for hidden reasoning, and a cut-off tool
 * call is treated as malformed, so an oversized plan would never be created at
 * all. Overrides are the expensive part — every category carries a 36-char
 * uuid, and a live drive that emitted Jan–Aug on 12 of 17 lines was cut off
 * three turns running — so they are capped per category
 * (`overridesPerCategory`, 6) as well as plan-wide (`monthOverrides`, 60). A
 * total plan is cheaper by construction: no map, one amount per line. The RPC
 * enforces the plan-wide numbers — change both or neither; the per-category cap
 * is model-facing only. `months: 60` bounds the window for BOTH kinds (a
 * five-year festival budget is the practical ceiling).
 *
 * ── DATES ───────────────────────────────────────────────────────────────────
 * `start_month` / `end_month` are calendar days that must be the FIRST of a
 * month (`firstOfMonthSchema`), matching how the budget tables key months
 * (`budget_month_allocations.month`, `envelope_budgets.start_month`); on an
 * envelope plan `end_month` may be null (open-ended), on a total plan it is
 * required. Override keys are `YYYY-MM` (`monthKeySchema`) — the handler
 * appends `-01` for the RPC.
 */
import { z } from "zod";
import { calendarDateSchema, monthSpan } from "./dateWindow.js";
/** The two values of `envelope_budgets.budget_type`. */
export const BUDGET_TYPES = ["envelope", "total"];
/**
 * Plan-size caps. The RPC (`create_budget_from_plan`) enforces the same
 * plan-wide numbers; `overridesPerCategory` is model-facing only.
 */
export const BUDGET_PLAN_LIMITS = {
    /** Category groups per plan. */
    groups: 12,
    /** Categories across the whole plan. */
    categories: 40,
    /** Per-month overrides across the whole plan (envelope plans only). */
    monthOverrides: 60,
    /**
     * Per-month overrides on ONE category — a seasonal line needs a handful,
     * not every month. Model-facing only (the RPC counts plan-wide).
     */
    overridesPerCategory: 6,
    /** Inclusive months from `start_month` to `end_month`. */
    months: 60,
};
/**
 * How far a total plan's expense lines may drift from the grand total the user
 * gave before `expected_expense_total` refuses it, in currency units. Rounding
 * a split across ~20 lines lands well inside a cent; anything larger is an
 * arithmetic slip, which is the whole point of the check.
 *
 * Compared in whole CENTS, not by `Math.abs(a - b) > 0.01` on the raw doubles:
 * 0.01 is not representable in IEEE 754, so a raw comparison accepts a one-cent
 * drift at some magnitudes and refuses it at others (accepted at 25,000;
 * refused at 100, 10,000 and 40,000). The amounts are money and the refusal
 * message quotes cents, so the boundary has to mean the same thing at every
 * size of budget.
 */
export const BUDGET_PLAN_TOTAL_TOLERANCE = 0.01;
const BUDGET_PLAN_TOTAL_TOLERANCE_CENTS = Math.round(BUDGET_PLAN_TOTAL_TOLERANCE * 100);
const toCents = (amount) => Math.round(amount * 100);
/** A real calendar day that is the first of its month. */
export const firstOfMonthSchema = calendarDateSchema.refine((value) => value.endsWith("-01"), "must be the first day of a month (yyyy-MM-01)");
/** A budgeted amount: finite, non-negative, inside numeric(15,2). */
export const money = z.number().finite().min(0).max(9_999_999_999_999);
/**
 * An override key: the calendar month as `YYYY-MM` (no day — the RPC gets `-01`).
 * The source string is exported so the assistant's tool JSON schema can restate
 * it as `propertyNames.pattern` without the two drifting.
 */
export const MONTH_KEY_PATTERN = "^\\d{4}-(0[1-9]|1[0-2])$";
export const monthKeySchema = z
    .string()
    .regex(new RegExp(MONTH_KEY_PATTERN), "must be a month as yyyy-MM");
/**
 * Sparse per-month overrides: `{ 'YYYY-MM': amount }`. A map (not an array
 * of `{month, budgeted}`) so a duplicate month is structurally impossible
 * and each override costs one key, not an object.
 */
export const budgetPlanMonthsSchema = z
    .record(monthKeySchema, money)
    .refine((months) => Object.keys(months).length <= BUDGET_PLAN_LIMITS.overridesPerCategory, {
    message: `at most ${BUDGET_PLAN_LIMITS.overridesPerCategory} month overrides per category — budget a line that varies every month at its average with no overrides`,
});
/** What both kinds of category carry. */
const budgetPlanCategoryBase = z.object({
    name: z.string().trim().min(1).max(80),
    /**
     * Model-facing key: the `chart_of_accounts.id` from `list_accounts` /
     * `get_pnl_monthly`. Verified in the caller's org and renamed
     * `gl_account_id` server-side. Lowercased so the duplicate check below
     * (which runs on parsed output) and the RPC both see one spelling —
     * Postgres emits lowercase, the model may not.
     */
    account_id: z
        .string()
        .uuid()
        .transform((s) => s.toLowerCase()),
    notes: z.string().trim().max(500).optional(),
});
/** A recurring (envelope) line: a monthly default plus sparse overrides. */
export const budgetPlanEnvelopeCategorySchema = budgetPlanCategoryBase
    .extend({
    default_budgeted: money,
    rollover_overspending: z.boolean().optional(),
    months: budgetPlanMonthsSchema.optional(),
    total_budgeted: z.undefined({
        invalid_type_error: "total_budgeted belongs to a total (project/fixed) budget; a recurring budget uses default_budgeted, the monthly amount",
    }),
})
    .strict();
/** A total (project/fixed) line: ONE amount for the whole period. */
export const budgetPlanTotalCategorySchema = budgetPlanCategoryBase
    .extend({
    total_budgeted: money,
    default_budgeted: z.undefined({
        invalid_type_error: "default_budgeted belongs to a recurring (envelope) budget; a total budget carries one total_budgeted for the whole period",
    }),
    months: z.undefined({
        invalid_type_error: "a total budget has no per-month overrides — put the whole-period amount in total_budgeted",
    }),
    rollover_overspending: z.undefined({
        invalid_type_error: "rollover only exists on a recurring (envelope) budget",
    }),
})
    .strict();
/**
 * Back-compat alias: the envelope category was the only category before
 * 2026-09-18, and this name is what existing imports use.
 */
export const budgetPlanCategorySchema = budgetPlanEnvelopeCategorySchema;
/** One factory, so the two arms' group shape cannot drift apart. */
const makeGroupSchema = (category) => z
    .object({
    name: z.string().trim().min(1).max(80),
    is_income: z.boolean(),
    categories: z.array(category).min(1),
})
    .strict();
export const budgetPlanGroupSchema = makeGroupSchema(budgetPlanEnvelopeCategorySchema);
export const budgetPlanTotalGroupSchema = makeGroupSchema(budgetPlanTotalCategorySchema);
/** Everything a plan carries regardless of kind. */
const budgetPlanBaseShape = {
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).optional(),
    start_month: firstOfMonthSchema,
    /**
     * Optional scope: ONE project of the caller's organisation. Absent OR NULL
     * means organisation-wide — `.nullish()`, not `.optional()`, for the same
     * reason the preprocess above tests `== null`: a model that has a field in
     * its schema and nothing to put there emits an explicit `null` rather than
     * dropping the key. The RPC documents and implements the same tolerance
     * (`absent/null ⇒ organisation-wide`), and the handler's `if (plan.project_id)`
     * already treats null as falsy, so refusing it here would have been a wrong
     * refusal for a semantically fine plan.
     */
    project_id: z
        .string()
        .uuid()
        .transform((s) => s.toLowerCase())
        .nullish(),
    /**
     * Fund scoping is a deliberate product refusal, not an oversight — so it gets
     * a message written at the model, like every other wrong-side field, instead
     * of `.strict()`'s "Unrecognized key(s)". `null` is accepted and ignored (the
     * RPC tolerates it too); any real fund id is refused.
     */
    fund_id: z
        .null({
        invalid_type_error: "fund-scoped budgets cannot be created here — create it on the Budgets page and choose the fund there, or leave fund_id out for an organisation-wide or project-scoped budget",
    })
        .optional(),
};
export const budgetPlanEnvelopeSchema = z
    .object({
    ...budgetPlanBaseShape,
    budget_type: z.literal("envelope"),
    /** Null or absent = open-ended. */
    end_month: firstOfMonthSchema.nullable().optional(),
    groups: z.array(budgetPlanGroupSchema).min(1).max(BUDGET_PLAN_LIMITS.groups),
    expected_expense_total: z.undefined({
        invalid_type_error: "expected_expense_total applies to a total (project/fixed) budget only — a recurring budget has no single grand total; show the annual total in the table instead",
    }),
})
    .strict();
export const budgetPlanTotalSchema = z
    .object({
    ...budgetPlanBaseShape,
    budget_type: z.literal("total"),
    /** REQUIRED and never null — a fixed amount needs a defined period. */
    end_month: firstOfMonthSchema,
    groups: z.array(budgetPlanTotalGroupSchema).min(1).max(BUDGET_PLAN_LIMITS.groups),
    /** The grand total the user gave, when they gave one. See the header. */
    expected_expense_total: money.optional(),
})
    .strict();
/**
 * Cross-field rules that hold for BOTH kinds, lifted out of the union so
 * neither arm can quietly lose one.
 */
const sharedPlanRefinements = (plan, ctx) => {
    if (plan.end_month) {
        if (plan.end_month < plan.start_month) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["end_month"],
                message: "end_month must not be before start_month",
            });
        }
        else if (monthSpan(plan.start_month, plan.end_month) > BUDGET_PLAN_LIMITS.months) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["end_month"],
                message: `budget must not span more than ${BUDGET_PLAN_LIMITS.months} months`,
            });
        }
    }
    // Widened once: both arms' groups share these fields, and iterating the raw
    // union would make every callback parameter a union too.
    const groups = plan.groups;
    const incomeGroups = groups.filter((g) => g.is_income).length;
    if (incomeGroups > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groups"],
            message: "at most one group may be is_income",
        });
    }
    let categoryCount = 0;
    let overrideCount = 0;
    const seenAccounts = new Set();
    groups.forEach((group, gi) => {
        group.categories.forEach((category, ci) => {
            categoryCount += 1;
            if (seenAccounts.has(category.account_id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["groups", gi, "categories", ci, "account_id"],
                    message: `account ${category.account_id} appears more than once`,
                });
            }
            seenAccounts.add(category.account_id);
            for (const key of Object.keys(category.months ?? {})) {
                overrideCount += 1;
                // The RPC refuses an override outside [start_month, end_month] too,
                // but one RAISE at a time; flag every offender here so the model
                // gets the whole list in one round. (A duplicate month cannot
                // happen — the map has one slot per key.)
                const month = `${key}-01`;
                if (month < plan.start_month || (plan.end_month && month > plan.end_month)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["groups", gi, "categories", ci, "months", key],
                        message: `month ${key} is outside the budget window for ${category.name}`,
                    });
                }
            }
        });
    });
    if (categoryCount > BUDGET_PLAN_LIMITS.categories) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groups"],
            message: `plan holds ${categoryCount} categories; the limit is ${BUDGET_PLAN_LIMITS.categories}`,
        });
    }
    if (overrideCount > BUDGET_PLAN_LIMITS.monthOverrides) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groups"],
            message: `plan holds ${overrideCount} month overrides; the limit is ${BUDGET_PLAN_LIMITS.monthOverrides}`,
        });
    }
    // Top-down reconciliation, total plans only. Income lines are excluded on
    // purpose: the figure the user names is what they intend to SPEND.
    if (plan.budget_type === "total" && plan.expected_expense_total !== undefined) {
        const spend = plan.groups
            .filter((g) => !g.is_income)
            .flatMap((g) => g.categories)
            .reduce((sum, c) => sum + c.total_budgeted, 0);
        if (Math.abs(toCents(spend) - toCents(plan.expected_expense_total)) >
            BUDGET_PLAN_TOTAL_TOLERANCE_CENTS) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["expected_expense_total"],
                message: `the expense lines total ${spend.toFixed(2)} but expected_expense_total is ${plan.expected_expense_total.toFixed(2)} — adjust the lines so they add up to the figure you showed the user, then present the corrected table`,
            });
        }
    }
};
/**
 * The plan as the model may write it. `budget_type` absent (or null) means
 * `'envelope'` — see the header for why this cannot be a `.default()` on the
 * literal.
 */
export const budgetPlanSchema = z
    .preprocess((value) => value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value.budget_type == null
    ? { ...value, budget_type: "envelope" }
    : value, z.discriminatedUnion("budget_type", [budgetPlanEnvelopeSchema, budgetPlanTotalSchema]))
    .superRefine(sharedPlanRefinements);
//# sourceMappingURL=budgets.js.map